import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './OperatorExpenses.css';
import { formatCurrency } from '../utils/formatters';
import CreateExpensePage from './CreateExpensePage';

const API = '/api/expenses';
const MASTER_DATA_API = '/api/master-data';
const UPLOAD_URL = '/api/uploads/';

/* ── Inline SVG Icons ── */
const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: ['M12 5v14', 'M5 12h14'],
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    eye: ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z', 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
};

export default function OperatorExpenses({ user: propUser, operatorDetails }) {
    const [expenses, setExpenses] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [categories, setCategories] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [clients, setClients] = useState([]);

    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [createPageOpen, setCreatePageOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const user = propUser || JSON.parse(localStorage.getItem('ops-user'));
    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : 'all');
    const country = operatorDetails?.country;
    const currency = operatorDetails?.defaultCurrency || 'USD';

    useEffect(() => { fetchExpenses(); fetchMasterData(); }, []);

    const fetchExpenses = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/${operatorId}`);
            setExpenses(res.data.expenses);
            setTotalAmount(res.data.totalAmount);
        } catch (err) { console.error('Fetch expenses error:', err); }
        finally { setLoading(false); }
    };

    const fetchMasterData = async () => {
        try {
            const [masterRes, vendorRes, vehicleRes, deptRes, driverRes, clientRes] = await Promise.all([
                axios.get(MASTER_DATA_API),
                axios.get(`/api/vendors/${operatorId}`),
                axios.get(`/api/vehicles/${operatorId}`),
                axios.get(`/api/departments/${operatorId}`),
                axios.get(`/api/drivers/${operatorId}`),
                axios.get(`/api/clients/${operatorId}`),
            ]);
            const grouped = masterRes.data.reduce((acc, item) => {
                const cat = item.category;
                if (!acc[cat]) acc[cat] = [];
                acc[cat].push(item);
                return acc;
            }, {});
            setCategories(grouped['expense-categories'] || []);
            setPaymentMethods(grouped['payment-methods'] || []);
            setVendors(vendorRes.data);
            setVehicles(vehicleRes.data);
            setDepartments(deptRes.data);
            setDrivers(driverRes.data);
            setClients(clientRes.data);
        } catch (err) { console.error('Fetch master data error:', err); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this expense?')) return;
        try { await axios.delete(`${API}/${id}`); fetchExpenses(); }
        catch (err) { console.error('Delete expense error:', err); }
    };

    const openCreate = (item = null) => { setEditItem(item); setCreatePageOpen(true); };

    const masterData = { categories, paymentMethods, vendors, vehicles, departments, drivers, clients, operatorId, country };

    /* ── Early return: full-page create/edit ── */
    if (createPageOpen) {
        return (
            <div className="operator-page" style={{ padding: 0 }}>
                <CreateExpensePage
                    editItem={editItem}
                    operatorDetails={operatorDetails}
                    masterData={masterData}
                    onBack={() => setCreatePageOpen(false)}
                    onSaved={() => { setCreatePageOpen(false); fetchExpenses(); }}
                />
            </div>
        );
    }

    /* ── Derived list data ── */
    const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const thisMonthTotal = expenses
        .filter(e => new Date(e.date) >= monthStart)
        .reduce((s, e) => s + (e.amount || 0), 0);

    const filtered = expenses.filter(exp =>
        exp.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exp.paymentMethod?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exp.notes?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    /* ── Category color map ── */
    const catColor = (cat = '') => {
        const map = { fuel: '#f59e0b', maintenance: '#ef4444', salary: '#6366f1', utilities: '#06b6d4', travel: '#10b981', office: '#8b5cf6' };
        const key = Object.keys(map).find(k => cat.toLowerCase().includes(k));
        return map[key] || '#64748b';
    };

    return (
        <div className="operator-page">
            {/* Breadcrumb */}
            <div className="op-breadcrumb">
                <span className="home-icon">🏠</span>
                <span className="sep">›</span>
                <span>Finance</span>
                <span className="sep">›</span>
                <span className="current">Expenses</span>
            </div>

            {/* Header */}
            <div className="op-header">
                <div className="op-header-left">
                    <h1 className="op-title">Business Expenses</h1>
                    <p className="op-subtitle">Track and manage your company's spending and financial outflows</p>
                </div>
            </div>

            {/* Stats */}
            <div className="op-stats-grid">
                <div className="op-stat-card">
                    <span className="op-stat-label">Total Expenses</span>
                    <span className="op-stat-value">{formatCurrency(totalAmount, country)}</span>
                </div>
                <div className="op-stat-card">
                    <span className="op-stat-label">This Month</span>
                    <span className="op-stat-value">{formatCurrency(thisMonthTotal, country)}</span>
                </div>
                <div className="op-stat-card">
                    <span className="op-stat-label">Transactions</span>
                    <span className="op-stat-value">{expenses.length}</span>
                </div>
            </div>

            {/* Actions bar */}
            <div className="op-actions-bar">
                <div className="op-search-box">
                    <span className="search-icon"><Icon d={icons.search} size={14} /></span>
                    <input type="text" placeholder="Search expenses by category, method, notes…"
                        value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                </div>
                <button className="op-create-btn" onClick={() => openCreate()}>
                    <Icon d={icons.plus} size={14} />
                    Record Expense
                </button>
            </div>

            {/* Table */}
            <div className="op-table-container">
                <table className="op-table">
                    <thead>
                        <tr>
                            <th>DATE</th>
                            <th>CATEGORY</th>
                            <th>PAYMENT METHOD</th>
                            <th>LINKED TO</th>
                            <th>AMOUNT</th>
                            <th>RECEIPT</th>
                            <th>ACTIONS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="7" style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>Loading expenses…</td></tr>
                        ) : paginated.length === 0 ? (
                            <tr><td colSpan="7" style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>
                                {searchTerm ? 'No expenses match your search' : 'No expenses recorded yet — click "Record Expense" to start'}
                            </td></tr>
                        ) : paginated.map(exp => (
                            <tr key={exp._id}>
                                <td style={{ fontWeight: 600, color: '#374151' }}>
                                    {new Date(exp.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </td>
                                <td>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 20, background: `${catColor(exp.category)}18`, color: catColor(exp.category), fontSize: 12, fontWeight: 700 }}>
                                        {exp.category}
                                    </span>
                                </td>
                                <td style={{ color: '#475569', fontSize: 13 }}>{exp.paymentMethod}</td>
                                <td>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}>
                                        {exp.vendorId?.vendorName && <span style={{ color: '#3b82f6', fontWeight: 600 }}>↳ {exp.vendorId.vendorName}</span>}
                                        {exp.clientId?.companyName && <span style={{ color: '#059669', fontWeight: 600 }}>↳ {exp.clientId.companyName}</span>}
                                        {exp.driverId?.name && <span style={{ color: '#d97706', fontWeight: 600 }}>↳ {exp.driverId.name}</span>}
                                        {exp.vehicleId?.plateNumber && <span style={{ color: '#7c3aed', fontWeight: 600 }}>↳ {exp.vehicleId.plateNumber}</span>}
                                        {exp.department && <span style={{ color: '#a21caf', fontWeight: 600 }}>↳ {exp.department}</span>}
                                        {!exp.vendorId?.vendorName && !exp.clientId?.companyName && !exp.driverId?.name && !exp.vehicleId?.plateNumber && !exp.department &&
                                            <span style={{ color: '#cbd5e1' }}>—</span>}
                                    </div>
                                </td>
                                <td style={{ fontWeight: 800, color: '#0f172a', fontSize: 15 }}>{formatCurrency(exp.amount, country)}</td>
                                <td>
                                    {exp.attachment
                                        ? <a href={`${UPLOAD_URL}${exp.attachment}`} target="_blank" rel="noreferrer"
                                            style={{ color: '#10b981', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600 }}>
                                            <Icon d={icons.eye} size={13} /> View
                                        </a>
                                        : <span style={{ color: '#cbd5e1', fontSize: 12 }}>None</span>}
                                </td>
                                <td className="inv-actions">
                                    <button title="Edit" onClick={() => openCreate(exp)}><Icon d={icons.edit} size={14} /></button>
                                    <button title="Delete" className="delete-btn" onClick={() => handleDelete(exp._id)}><Icon d={icons.trash} size={14} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {totalPages > 1 && (
                    <div className="op-pagination">
                        <div className="page-info">
                            Showing {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length}
                        </div>
                        <div className="page-controls">
                            <button className="page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Previous</button>
                            {[...Array(totalPages)].map((_, i) => (
                                <button key={i} className={`page-btn ${currentPage === i + 1 ? 'active' : ''}`} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                            ))}
                            <button className="page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
